import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NEN 7510: Session timeout configuration (15 minutes = 900000ms)
const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 60 minuten sessie timeout (was 15 min)
const TOKEN_REFRESH_INTERVAL_MS = 55 * 60 * 1000; // Refresh 5 minuten voor expiry (was 12 min)
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpiring, setSessionExpiring] = useState(false);
  
  const lastActivityRef = useRef(Date.now());
  const sessionTimeoutRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  // NEN 7510: Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSessionExpiring(false);
  }, []);

  // NEN 7510: Check for inactivity and handle session timeout
  const checkInactivity = useCallback(() => {
    const inactiveTime = Date.now() - lastActivityRef.current;
    
    // Show warning 1 minute before session expires
    if (inactiveTime >= SESSION_TIMEOUT_MS - 60000 && !sessionExpiring) {
      setSessionExpiring(true);
      toast.warning('Uw sessie verloopt over 1 minuut door inactiviteit', {
        duration: 10000,
        action: {
          label: 'Verlengen',
          onClick: () => {
            updateActivity();
            refreshToken();
          }
        }
      });
    }
    
    // Auto logout after session timeout
    if (inactiveTime >= SESSION_TIMEOUT_MS) {
      toast.error('Sessie verlopen door inactiviteit (NEN 7510)');
      logout();
    }
  }, [sessionExpiring, updateActivity]);

  // NEN 7510: Refresh token before expiry
  const refreshToken = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh-token`);
      const newToken = response.data.token;
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error.response?.status === 401) {
        toast.error('Sessie verlopen, log opnieuw in');
        logout();
      }
    }
  }, [token]);

  // Setup activity listeners and timers
  useEffect(() => {
    if (token && user) {
      // Add activity listeners
      ACTIVITY_EVENTS.forEach(event => {
        window.addEventListener(event, updateActivity, { passive: true });
      });

      // Start session timeout checker (every 30 seconds)
      sessionTimeoutRef.current = setInterval(checkInactivity, 30000);
      
      // Start token refresh interval (every 12 minutes)
      refreshIntervalRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL_MS);

      return () => {
        ACTIVITY_EVENTS.forEach(event => {
          window.removeEventListener(event, updateActivity);
        });
        if (sessionTimeoutRef.current) clearInterval(sessionTimeoutRef.current);
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      };
    }
  }, [token, user, updateActivity, checkInactivity, refreshToken]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      lastActivityRef.current = Date.now();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    
    if (response.data.requires_2fa) {
      return { requires_2fa: true, email: response.data.email };
    }
    
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return { success: true };
  };

  const verify2FA = async (email, code) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-2fa`, { email, code });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return { success: true };
  };

  const register = async (data) => {
    await axios.post(`${API_URL}/api/auth/register`, data);
    return { success: true };
  };

  const registerWithInvite = async (data) => {
    await axios.post(`${API_URL}/api/auth/register-with-invite`, data);
    return { success: true };
  };

  // NEN 7510: Logout with server-side logging
  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/api/auth/logout`);
      }
    } catch (error) {
      console.error('Logout logging failed:', error);
    } finally {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      setSessionExpiring(false);
      
      // Clear all timers
      if (sessionTimeoutRef.current) clearInterval(sessionTimeoutRef.current);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    }
  };

  const setup2FA = async () => {
    const response = await axios.post(`${API_URL}/api/auth/setup-2fa`);
    return response.data;
  };

  const enable2FA = async (code) => {
    await axios.post(`${API_URL}/api/auth/enable-2fa?code=${code}`);
    setUser({ ...user, totp_enabled: true });
  };

  // NEN 7510: Get password requirements
  const getPasswordRequirements = async () => {
    const response = await axios.get(`${API_URL}/api/auth/password-requirements`);
    return response.data;
  };

  // NEN 7510: Extend session manually
  const extendSession = async () => {
    updateActivity();
    await refreshToken();
    toast.success('Sessie verlengd met 15 minuten');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      verify2FA, 
      register, 
      registerWithInvite, 
      logout, 
      setup2FA, 
      enable2FA,
      isAdmin: user?.role === 'admin',
      sessionExpiring,
      extendSession,
      getPasswordRequirements
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
